import React, { useEffect, useState } from 'react';
import AdminMerchantSearchPicker from '../components/AdminMerchantSearchPicker';
import { fetchAdminMerchant, patchAdminMerchantTier } from '../services/adminApi';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';

const TIERS = ['free', 'starter', 'pro', 'enterprise'];

function TierBadge({ children }) {
    return (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
            {children}
        </span>
    );
}

export default function LoyalPassAdminMerchantTierPage() {
    const [selectedMerchantId, setSelectedMerchantId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [tierDraft, setTierDraft] = useState('free');
    const [saveBusy, setSaveBusy] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(null);

    useEffect(() => {
        if (!selectedMerchantId) {
            setDetail(null);
            setDetailError(null);
            setSaveError(null);
            setSaveSuccess(null);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setDetailLoading(true);
            setDetailError(null);
            setSaveError(null);
            setSaveSuccess(null);
            try {
                const res = await fetchAdminMerchant(selectedMerchantId);
                if (!cancelled) {
                    setDetail(res.data);
                    setTierDraft(res.data?.merchant?.tier || 'free');
                }
            } catch (e) {
                if (!cancelled) {
                    setDetail(null);
                    setDetailError(extractErrorMessage(e) || 'Failed to load merchant');
                }
            } finally {
                if (!cancelled) {
                    setDetailLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [selectedMerchantId]);

    const handleSaveTier = async () => {
        if (!selectedMerchantId) {
            return;
        }
        setSaveBusy(true);
        setSaveError(null);
        setSaveSuccess(null);
        try {
            const patchRes = await patchAdminMerchantTier(selectedMerchantId, { tier: tierDraft });
            const res = await fetchAdminMerchant(selectedMerchantId);
            setDetail(res.data);
            const stripeMessages = patchRes.data?.stripeSync?.messages || [];
            const base =
                patchRes.data?.stripeSync?.action === 'canceled_subscription'
                    ? 'Tier saved. Stripe subscription canceled and account set to free.'
                    : patchRes.data?.stripeSync?.action === 'updated_subscription'
                      ? 'Tier saved. Stripe subscription updated to match.'
                      : 'Tier saved.';
            setSaveSuccess(
                stripeMessages.length > 0 ? `${base} ${stripeMessages.join(' ')}` : base
            );
        } catch (e) {
            setSaveError(extractErrorMessage(e) || 'Failed to update tier');
        } finally {
            setSaveBusy(false);
        }
    };

    const merchant = detail?.merchant;
    const billing = detail?.billing;
    const currentTier = merchant?.tier || 'free';
    const unchanged = tierDraft === currentTier;

    return (
        <div className="p-6 max-w-2xl mx-auto">
            <h1 className="text-2xl font-semibold text-slate-900">Merchant Tiers</h1>
            <p className="text-slate-600 mt-2 text-sm">
                Search for a merchant, then set their plan tier. Saving updates LoyalPass and syncs Stripe when
                configured: downgrades to <strong>free</strong> cancel an active subscription; paid tiers with an
                existing subscription update the Stripe price. Merchants without a subscription must still checkout
                in Membership unless you bill them outside Stripe.
            </p>

            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Find merchant</h2>
                <AdminMerchantSearchPicker
                    autoLoadRecent
                    selectedMerchantId={selectedMerchantId}
                    onSelectMerchant={(id) => setSelectedMerchantId(id)}
                />
            </div>

            {selectedMerchantId && detailLoading && (
                <p className="mt-6 text-sm text-slate-600">Loading merchant…</p>
            )}

            {detailError && (
                <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {detailError}
                </div>
            )}

            {merchant && !detailLoading && (
                <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
                    <h2 className="text-sm font-semibold text-slate-800">Selected merchant</h2>
                    <div className="text-sm space-y-1">
                        <div>
                            <span className="text-slate-500">Business </span>
                            <span className="font-medium text-slate-900">{merchant.name || '—'}</span>
                        </div>
                        <div>
                            <span className="text-slate-500">Email </span>
                            <span>{merchant.email || '—'}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-slate-500">Current tier </span>
                            <TierBadge>{currentTier}</TierBadge>
                            {billing?.stripeTier && billing.stripeTier !== currentTier && (
                                <span className="text-xs text-amber-700">
                                    Stripe maps to {billing.stripeTier}
                                </span>
                            )}
                        </div>
                        {billing?.stripeCustomerId && (
                            <p className="text-xs text-slate-500 mt-2 break-all">
                                Stripe customer: {billing.stripeCustomerId}
                            </p>
                        )}
                        {billing?.stripeSubscriptionId && (
                            <p className="text-xs text-slate-500 break-all">
                                Stripe subscription: {billing.stripeSubscriptionId}
                                {billing.subscriptionStatus
                                    ? ` (${billing.subscriptionStatus})`
                                    : ''}
                            </p>
                        )}
                    </div>

                    {billing?.mismatch && billing.mismatchReason && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                            <strong>Billing mismatch:</strong> {billing.mismatchReason}
                        </div>
                    )}

                    {billing?.configWarnings?.length > 0 && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                            <p className="font-medium text-slate-700 mb-1">Server Stripe price config</p>
                            <ul className="list-disc pl-4 space-y-0.5">
                                {billing.configWarnings.map((w) => (
                                    <li key={w}>{w}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="pt-2 border-t border-slate-100">
                        <label htmlFor="admin-tier-select" className="block text-sm font-medium text-slate-700 mb-2">
                            New tier
                        </label>
                        <div className="flex flex-wrap items-center gap-2">
                            <select
                                id="admin-tier-select"
                                value={tierDraft}
                                onChange={(e) => setTierDraft(e.target.value)}
                                disabled={saveBusy}
                                className="rounded-md border border-slate-300 bg-white px-2 py-2 text-sm text-slate-800 min-w-[11rem]"
                            >
                                {TIERS.map((t) => (
                                    <option key={t} value={t}>
                                        {t.charAt(0).toUpperCase() + t.slice(1)}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={handleSaveTier}
                                disabled={saveBusy || unchanged}
                                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {saveBusy ? 'Saving…' : 'Save tier'}
                            </button>
                        </div>
                    </div>

                    {saveError && (
                        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                            {saveError}
                        </div>
                    )}
                    {saveSuccess && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                            {saveSuccess}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
